import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Music, Pencil, Printer, Save, Send } from 'lucide-react';
import {
  fetchReservationsConcertBureau,
  modifierReservationConcert,
  renvoyerConfirmationConcert,
  updateEvenement,
} from '../services/api';
import { nomFamille, prenomUsage } from '../utils/nom';
import { imprimerListe, type ColonneImpression } from '../utils/impression';

import { dateLongueHeureFr } from '../utils/dates';
const euros = (montant: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
    .format(montant);

const statutLabels: Record<string, { label: string; classe: string }> = {
  payee: { label: 'Confirmée', classe: 'bg-green-50 text-green-700' },
  en_attente: { label: 'En attente', classe: 'bg-amber-50 text-amber-700' },
  annulee: { label: 'Annulée', classe: 'bg-gray-100 text-gray-500 dark:text-gray-400 dark:bg-gray-700' },
};

const dateLisible = (iso?: string | null) =>
  iso ? dateLongueHeureFr(iso) : '';

/**
 * Colonnes de la feuille de contrôle à l'entrée.
 *
 * Le code de retrait accompagne la référence : c'est lui que le spectateur
 * présente à la porte, et le seul repère quand le réseau manque.
 */
const colonnesImpression: ColonneImpression<any>[] = [
  {
    titre: 'Référence',
    valeur: (r) => [r.reference, r.codeRetrait].filter(Boolean).join('\n'),
  },
  { titre: 'Spectateur', valeur: (r) => [r.prenom, r.nom].filter(Boolean).join(' ') },
  { titre: 'Téléphone', valeur: (r) => r.telephone ?? '' },
  { titre: 'Pay.', valeur: (r) => String(r.nbPayantes ?? 0), alignement: 'centre' },
  { titre: 'Grat.', valeur: (r) => String(r.nbGratuites ?? 0), alignement: 'centre' },
  { titre: 'Montant', valeur: (r) => euros(r.montantTotal ?? 0), alignement: 'droite' },
  { titre: 'Statut', valeur: (r) => statutLabels[r.statut]?.label ?? r.statut ?? '' },
];

/** Les champs date attendent le format accepté par datetime-local. */
const pourChamp = (iso?: string | null) => (iso ? iso.slice(0, 16) : '');

/**
 * Concert d'un événement : tarif, jauge et liste des réservations.
 *
 * La jauge est la seule chose qui ferme le bouton public : une fois les places
 * vendues, le visiteur voit « Complet » sans intervention du bureau.
 */
export default function OngletConcert({
  evenementId,
  evenement,
  onSucces,
  onEchec,
}: {
  evenementId: number;
  evenement: any;
  onSucces: (message: string) => void;
  onEchec: (error: Error) => void;
}) {
  const queryClient = useQueryClient();

  const [form, setForm] = useState({
    concertActif: false,
    concertDescription: '',
    concertPrixPlace: 0,
    concertQuotaPayant: 0,
    concertQuotaGratuit: 0,
    concertDate: '',
    concertDateLimite: '',
  });

  // Le formulaire porte l'état enregistré dès qu'il est connu : partir de zéro
  // remettrait la jauge à néant au premier enregistrement.
  useEffect(() => {
    if (!evenement) return;
    setForm({
      concertActif: evenement.concertActif === true,
      concertDescription: evenement.concertDescription ?? '',
      concertPrixPlace: evenement.concertPrixPlace ?? 0,
      concertQuotaPayant: evenement.concertQuotaPayant ?? 0,
      concertQuotaGratuit: evenement.concertQuotaGratuit ?? 0,
      concertDate: pourChamp(evenement.concertDate),
      concertDateLimite: pourChamp(evenement.concertDateLimite),
    });
  }, [evenement]);

  const query = useQuery({
    queryKey: ['evenement-concert', evenementId],
    queryFn: () => fetchReservationsConcertBureau(evenementId),
  });

  const enregistrer = useMutation({
    mutationFn: () =>
      updateEvenement(evenementId, {
        ...form,
        concertDate: form.concertDate || null,
        concertDateLimite: form.concertDateLimite || null,
        concertDescription: form.concertDescription.trim() || null,
      }),
    onSuccess: () => {
      onSucces('Concert mis à jour.');
      queryClient.invalidateQueries({ queryKey: ['evenement-concert', evenementId] });
      queryClient.invalidateQueries({ queryKey: ['bureau-evenements'] });
    },
    onError: onEchec,
  });

  const etat = query.data?.concert;
  const totaux = query.data?.totaux;
  const reservations: any[] = query.data?.reservations ?? [];

  return (
    <div className="space-y-5">
      <section className="card lg:p-5 space-y-4">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.concertActif}
            onChange={(e) => setForm({ ...form, concertActif: e.target.checked })}
            className="mt-0.5"
          />
          <span>
            <span className="font-medium">Proposer un concert sur réservation</span>
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              Le bloc de réservation n'apparaît sur la page publique que si l'événement est publié.
            </span>
          </span>
        </label>

        {form.concertActif && (
          <>
            <textarea
              className="input min-h-[4rem]"
              placeholder="Programme, artistes, ouverture des portes…"
              value={form.concertDescription}
              onChange={(e) => setForm({ ...form, concertDescription: e.target.value })}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <label className="text-sm text-gray-600 dark:text-gray-400">
                Prix de la place (€)
                <input
                  className="input mt-1"
                  type="number"
                  min={0}
                  value={form.concertPrixPlace}
                  onChange={(e) => setForm({ ...form, concertPrixPlace: Number(e.target.value) })}
                />
              </label>
              <label className="text-sm text-gray-600 dark:text-gray-400">
                Places payantes
                <input
                  className="input mt-1"
                  type="number"
                  min={0}
                  value={form.concertQuotaPayant}
                  onChange={(e) => setForm({ ...form, concertQuotaPayant: Number(e.target.value) })}
                />
              </label>
              <label className="text-sm text-gray-600 dark:text-gray-400">
                Places gratuites
                <input
                  className="input mt-1"
                  type="number"
                  min={0}
                  value={form.concertQuotaGratuit}
                  onChange={(e) => setForm({ ...form, concertQuotaGratuit: Number(e.target.value) })}
                />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Enfants, bénévoles, invités. Elles ne rapportent rien mais occupent un siège :
                  leur quota est distinct pour ne pas manger la jauge payante.
                </span>
              </label>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <label className="text-sm text-gray-600 block dark:text-gray-400">
                Date et heure du concert <span className="text-red-600">*</span>
                <input
                  className="input mt-1"
                  type="datetime-local"
                  value={form.concertDate}
                  onChange={(e) => setForm({ ...form, concertDate: e.target.value })}
                />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Libre, sans lien avec les dates de l'événement. C'est cette heure que le
                  spectateur lit sur sa confirmation.
                </span>
              </label>

              <label className="text-sm text-gray-600 block dark:text-gray-400">
                Clôture des réservations (facultatif)
                <input
                  className="input mt-1"
                  type="datetime-local"
                  value={form.concertDateLimite}
                  max={form.concertDate || undefined}
                  onChange={(e) => setForm({ ...form, concertDateLimite: e.target.value })}
                />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Passée cette date, le bouton se ferme même s'il reste des places : la
                  billetterie se tient alors à l'entrée.
                </span>
              </label>
            </div>
          </>
        )}

        <button
          onClick={() => enregistrer.mutate()}
          disabled={enregistrer.isPending}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          {enregistrer.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Enregistrer
        </button>
      </section>

      {etat?.actif && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Indicateur libelle="Réservations" valeur={String(totaux?.reservations ?? 0)} />
            <Indicateur
              libelle="Places vendues"
              valeur={String(totaux?.places ?? 0)}
              badges={[
                {
                  texte: `dont ${totaux?.gratuites ?? 0} gratuite${
                    (totaux?.gratuites ?? 0) > 1 ? 's' : ''
                  }`,
                  classe:
                    'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
                },
              ]}
            />
            <Indicateur
              libelle="Places restantes"
              valeur={String(etat.restantTotal)}
              badges={[
                {
                  texte: `${etat.restantPayant} payante${etat.restantPayant > 1 ? 's' : ''}`,
                  classe:
                    'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
                },
                {
                  texte: `${etat.restantGratuit} gratuite${etat.restantGratuit > 1 ? 's' : ''}`,
                  classe:
                    'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
                },
              ]}
              accent={etat.complet ? 'text-red-600' : 'text-gray-900 dark:text-gray-100'}
            />
            <Indicateur
              libelle="Encaissé"
              valeur={euros(totaux?.encaisse ?? 0)}
              accent="text-green-600"
            />
          </div>

          {etat.complet && (
            <p className="text-sm rounded-xl px-3 py-2 bg-red-50 text-red-700">
              Le concert affiche « Complet » sur la page publique. Augmentez la jauge pour
              rouvrir les réservations.
            </p>
          )}

          {/* À la porte, on coche les entrées sur le papier : le réseau y est
              rarement fiable, et la file n'attend pas. */}
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              Réservations
            </h3>
            <button
              type="button"
              onClick={() =>
                imprimerListe({
                  titre: 'Concert — liste des réservations',
                  sousTitre: [evenement?.nom, dateLisible(evenement?.concertDate)]
                    .filter(Boolean)
                    .join(' — '),
                  totaux: [
                    { libelle: 'réservations', valeur: String(totaux?.reservations ?? 0) },
                    { libelle: 'places', valeur: String(totaux?.places ?? 0) },
                    { libelle: 'encaissé', valeur: euros(totaux?.encaisse ?? 0) },
                  ],
                  colonnes: colonnesImpression,
                  lignes: reservations,
                  colonnePointage: 'Entré',
                })
              }
              disabled={reservations.length === 0}
              className="btn-outline inline-flex items-center gap-1.5 text-sm px-3 py-1.5 disabled:opacity-50"
            >
              <Printer className="w-4 h-4" />
              Imprimer la liste
            </button>
          </div>

          <section className="card p-0 overflow-x-auto">
            {query.isPending ? (
              <div className="p-6 flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
              </div>
            ) : reservations.length === 0 ? (
              <p className="p-6 text-center text-gray-500 text-sm dark:text-gray-400">
                Aucune réservation.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 text-left text-gray-600 dark:text-gray-300">
                  <tr>
                    <th className="px-4 py-3 font-medium">Référence</th>
                    <th className="px-4 py-3 font-medium">Spectateur</th>
                    <th className="px-4 py-3 font-medium text-center">Pay.</th>
                    <th className="px-4 py-3 font-medium text-center">Grat.</th>
                    <th className="px-4 py-3 font-medium text-right">Montant</th>
                    <th className="px-4 py-3 font-medium">Statut</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {reservations.map((r) => (
                    <LigneReservation
                      key={r.id}
                      reservation={r}
                      onSucces={onSucces}
                      onEchec={onEchec}
                      onModifie={() =>
                        queryClient.invalidateQueries({
                          queryKey: ['evenement-concert', evenementId],
                        })
                      }
                    />
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}

      {!etat?.actif && (
        <p className="text-sm text-gray-500 flex items-center gap-2 dark:text-gray-400">
          <Music className="w-4 h-4 text-gray-400" />
          Activez le concert pour ouvrir les réservations et suivre la billetterie.
        </p>
      )}
    </div>
  );
}

/**
 * Une réservation, en lecture ou en correction.
 *
 * Les places et le montant ne sont pas modifiables : ils correspondent à un
 * paiement encaissé.
 */
function LigneReservation({
  reservation: r,
  onSucces,
  onEchec,
  onModifie,
}: {
  reservation: any;
  onSucces: (message: string) => void;
  onEchec: (error: Error) => void;
  onModifie: () => void;
}) {
  const [edition, setEdition] = useState(false);
  const [form, setForm] = useState({
    nom: r.nom ?? '',
    prenom: r.prenom ?? '',
    email: r.email ?? '',
    telephone: r.telephone ?? '',
    commentaire: r.commentaire ?? '',
  });

  const enregistrer = useMutation({
    mutationFn: () =>
      modifierReservationConcert(r.id, {
        nom: form.nom.trim(),
        prenom: form.prenom.trim() || null,
        email: form.email.trim(),
        telephone: form.telephone.trim() || null,
        commentaire: form.commentaire.trim() || null,
      }),
    onSuccess: () => {
      onSucces('Réservation corrigée.');
      setEdition(false);
      onModifie();
    },
    onError: onEchec,
  });

  const renvoyer = useMutation({
    mutationFn: () => renvoyerConfirmationConcert(r.id),
    onSuccess: (d: any) => onSucces(d?.message ?? 'Confirmation renvoyée.'),
    onError: onEchec,
  });

  const statut = statutLabels[r.statut] ?? {
    label: r.statut,
    classe: 'bg-gray-100 text-gray-600 dark:text-gray-400 dark:bg-gray-700',
  };

  if (edition) {
    return (
      <tr className="bg-primary-50/40 dark:bg-primary-900/10">
        <td className="px-4 py-3 font-mono text-xs align-top">{r.reference}</td>
        <td className="px-4 py-3" colSpan={5}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              className="input"
              placeholder="Nom *"
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: nomFamille(e.target.value) })}
            />
            <input
              className="input"
              placeholder="Prénom"
              value={form.prenom}
              onChange={(e) => setForm({ ...form, prenom: prenomUsage(e.target.value) })}
            />
            <input
              className="input"
              type="email"
              placeholder="E-mail *"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <input
              className="input"
              type="tel"
              placeholder="Téléphone"
              value={form.telephone}
              onChange={(e) => setForm({ ...form, telephone: e.target.value })}
            />
          </div>
          <textarea
            className="input min-h-[3rem] mt-2"
            placeholder="Accessibilité, remarque…"
            value={form.commentaire}
            onChange={(e) => setForm({ ...form, commentaire: e.target.value })}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Les places et le montant ne se corrigent pas ici : ils correspondent à un paiement
            encaissé.
          </p>
        </td>
        <td className="px-4 py-3 align-top text-right whitespace-nowrap">
          <button
            onClick={() => enregistrer.mutate()}
            disabled={
              enregistrer.isPending || form.nom.trim() === '' || !form.email.includes('@')
            }
            className="btn-primary px-3 py-1.5 text-sm inline-flex items-center gap-1 disabled:opacity-50"
          >
            {enregistrer.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5" />
            )}
            Enregistrer
          </button>
          <button
            onClick={() => setEdition(false)}
            className="block mt-1 ml-auto text-xs text-gray-500 dark:text-gray-400"
          >
            Annuler
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td className="px-4 py-3 font-mono text-xs">
        {r.reference}
        {r.codeRetrait && (
          <div className="text-[11px] text-gray-400 dark:text-gray-500">{r.codeRetrait}</div>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="font-medium">{[r.prenom, r.nom].filter(Boolean).join(' ')}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{r.email}</div>
        {r.telephone && (
          <div className="text-xs text-gray-500 dark:text-gray-400">{r.telephone}</div>
        )}
        {r.commentaire && (
          <div className="text-xs text-amber-700 italic mt-0.5">{r.commentaire}</div>
        )}
      </td>
      <td className="px-4 py-3 text-center tabular-nums">{r.nbPayantes}</td>
      <td className="px-4 py-3 text-center tabular-nums">{r.nbGratuites}</td>
      <td className="px-4 py-3 text-right tabular-nums">{euros(r.montantTotal)}</td>
      <td className="px-4 py-3">
        <span className={`text-xs px-2 py-0.5 rounded-full ${statut.classe}`}>{statut.label}</span>
        {r.entreesValidees > 0 && (
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
            {r.entreesValidees}/{r.nbPlaces} entrée(s) validée(s)
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <button
          onClick={() => setEdition(true)}
          title="Corriger les coordonnées"
          aria-label="Corriger les coordonnées"
          className="p-1.5 rounded-lg text-gray-500 hover:text-primary-700 hover:bg-primary-50 dark:text-gray-400 dark:hover:bg-gray-700"
        >
          <Pencil className="w-4 h-4" />
        </button>
        {/* Le renvoi n'a de sens que sur une réservation confirmée : c'est elle
            seule qui porte un code d'entrée. */}
        {r.statut === 'payee' && (
          <button
            onClick={() => renvoyer.mutate()}
            disabled={renvoyer.isPending}
            title="Renvoyer la confirmation au spectateur"
            aria-label="Renvoyer la confirmation au spectateur"
            className="p-1.5 rounded-lg text-gray-500 hover:text-primary-700 hover:bg-primary-50 disabled:opacity-40 dark:text-gray-400 dark:hover:bg-gray-700"
          >
            {renvoyer.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        )}
      </td>
    </tr>
  );
}

/**
 * Un chiffre, et le cas échéant sa décomposition en pastilles.
 *
 * Chaque catégorie a sa couleur : le regard sépare payantes et gratuites sans
 * lire. Le total reste nu, une pastille le mettrait sur le même plan que ses
 * parties.
 */
function Indicateur({
  libelle,
  valeur,
  badges,
  accent = 'text-gray-900 dark:text-gray-100',
}: {
  libelle: string;
  valeur: string;
  badges?: { texte: string; classe: string }[];
  accent?: string;
}) {
  return (
    <div className="card py-3 text-center">
      <p className="text-xs text-gray-500 dark:text-gray-400">{libelle}</p>
      <p className={`text-lg lg:text-xl font-bold tabular-nums ${accent}`}>{valeur}</p>
      {badges && badges.length > 0 && (
        <div className="mt-1 flex flex-wrap justify-center gap-1">
          {badges.map((b) => (
            <span
              key={b.texte}
              className={`text-[11px] font-medium px-1.5 py-0.5 rounded-full tabular-nums ${b.classe}`}
            >
              {b.texte}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
